import { useEffect, useState, useRef } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import { setTokenGetter } from '@/lib/api';
import { startCheckout } from '@/lib/checkout';

// ---------------------------------------------------------------------------
// CheckoutResumePage
// ---------------------------------------------------------------------------
// Dedicated post-signup landing page that finishes the upgrade flow.
//
// Why this exists: when a guest clicks "Continue to Checkout" while signed
// out, we send them through Clerk Sign In/Sign Up first. Once Clerk reports
// a session, we need to (a) wait for a usable JWT to be issued and (b) call
// /api/create-checkout-session, then redirect to Stripe. Doing this on a
// dedicated route gives the user visible feedback, avoids brittle
// sessionStorage tricks, and gives us an obvious retry button if anything
// goes wrong.
// ---------------------------------------------------------------------------

function getPriceIdFromUrl() {
  try {
    return new URLSearchParams(window.location.search).get('priceId') || 'monthly';
  } catch {
    return 'monthly';
  }
}

// Shared presentational layer.
function ResumeUI({ status, errorMsg, onRetry }) {
  return (
    <div
      className="min-h-screen bg-white flex items-center justify-center px-6"
      data-testid="checkout-resume-page"
    >
      <div className="max-w-md w-full text-center">
        {(status === 'preparing' || status === 'redirecting') && (
          <>
            <div className="mx-auto w-10 h-10 mb-6">
              <div className="w-10 h-10 rounded-full border-2 border-black/15 border-t-black animate-spin" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-black">
              {status === 'preparing'
                ? 'Setting up your checkout…'
                : 'Redirecting to Stripe…'}
            </h1>
            <p className="mt-3 text-sm text-gray-600">
              You'll be on the secure payment page in a moment.
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-black">
              Something went wrong.
            </h1>
            <p className="mt-3 text-sm text-gray-600">
              {errorMsg || 'We could not start your checkout.'}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={onRetry}
                data-testid="checkout-resume-retry-btn"
                className="bg-black text-white hover:bg-black/85 rounded-md px-6"
              >
                Try again
              </Button>
              <Button
                variant="outline"
                onClick={() => { window.location.href = '/app'; }}
                data-testid="checkout-resume-cancel-btn"
                className="border-black/20 text-black hover:bg-black/5 rounded-md px-6"
              >
                Back to app
              </Button>
            </div>
          </>
        )}

        {status === 'unauthenticated' && (
          <>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-black">
              Sign in to continue
            </h1>
            <p className="mt-3 text-sm text-gray-600">
              Your sign-in didn't complete. Head back to the app and try again.
            </p>
            <div className="mt-8">
              <Button
                onClick={() => { window.location.href = '/app'; }}
                data-testid="checkout-resume-home-btn"
                className="bg-black text-white hover:bg-black/85 rounded-md px-6"
              >
                Back to app
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Real Clerk-aware variant. Mounted only when ClerkProvider is in the tree.
function ClerkResume() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const [status, setStatus] = useState('preparing');
  const [errorMsg, setErrorMsg] = useState('');
  const startedRef = useRef(false);
  const priceId = getPriceIdFromUrl();

  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn) {
      setStatus('unauthenticated');
      return;
    }

    if (startedRef.current) return; // guard against StrictMode double-effect
    startedRef.current = true;

    setTokenGetter(getToken);

    (async () => {
      // Wait for a real JWT to be available. Fresh signups occasionally
      // need a beat before getToken() returns a non-null value.
      let token = null;
      for (let i = 0; i < 8; i++) {
        try {
          token = await getToken();
        } catch (_) {
          token = null;
        }
        if (token) break;
        await new Promise((r) => setTimeout(r, 250));
      }

      if (!token) {
        setErrorMsg('Could not verify your session. Please try again.');
        setStatus('error');
        return;
      }

      setStatus('redirecting');
      const ok = await startCheckout(priceId);
      if (!ok) {
        setErrorMsg('We could not open Stripe Checkout. Please try again.');
        setStatus('error');
      }
      // If `ok` is true, the browser is redirecting to Stripe — nothing more
      // to do.
    })();
  }, [isLoaded, isSignedIn, getToken, priceId, user]);

  const retry = async () => {
    setErrorMsg('');
    setStatus('redirecting');
    const ok = await startCheckout(priceId);
    if (!ok) {
      setErrorMsg('We could not open Stripe Checkout. Please try again.');
      setStatus('error');
    }
  };

  return <ResumeUI status={status} errorMsg={errorMsg} onRetry={retry} />;
}

// Dev-mode variant: no Clerk, calls startCheckout directly. The backend will
// reject with 401 (no token) which surfaces as an "Authentication required"
// error in the UI — useful for verifying the page renders & wires correctly.
function DevResume() {
  const [status, setStatus] = useState('preparing');
  const [errorMsg, setErrorMsg] = useState('');
  const startedRef = useRef(false);
  const priceId = getPriceIdFromUrl();

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      setStatus('redirecting');
      const ok = await startCheckout(priceId);
      if (!ok) {
        setErrorMsg('Authentication required to start checkout.');
        setStatus('error');
      }
    })();
  }, [priceId]);

  const retry = async () => {
    setErrorMsg('');
    setStatus('redirecting');
    const ok = await startCheckout(priceId);
    if (!ok) {
      setErrorMsg('Authentication required to start checkout.');
      setStatus('error');
    }
  };

  return <ResumeUI status={status} errorMsg={errorMsg} onRetry={retry} />;
}

export default function CheckoutResumePage({ devMode = false }) {
  return devMode ? <DevResume /> : <ClerkResume />;
}
